import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { laboratoryClient } from 'pricing-pages-ui/utils/laboratoryClient';
import { jsx as _jsx } from "react/jsx-runtime";
const ExperimentContext = /*#__PURE__*/createContext(null);

/**
 * Resolves frontend experiments via the shared `laboratoryClient` singleton.
 *
 * Copied from pricing-pages-ui/context/ExperimentContext — do not import from there
 * once Phase 3 consumers migrate to this provider.
 *
 * `laboratoryClient` itself still lives in pricing-pages-ui until #19827 (public-app
 * infra) lands and copies it locally; until then, this provider intentionally
 * imports the singleton from there so both apps share the same resolved-treatments
 * cache.
 */
export const ExperimentProvider = ({
  children
}) => {
  const [treatments, setTreatments] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [eligibleTreatments, setEligibleTreatments] = useState([]);
  useEffect(() => {
    laboratoryClient.resolve(resolved => {
      setTreatments(resolved);
      setIsLoading(false);
    });
  }, []);
  const registerEligibility = useCallback(treatment => {
    setEligibleTreatments(prev => {
      const withoutCurrent = prev.filter(t => t.treatmentKey !== treatment.treatmentKey);
      if (!treatment.isEligible) {
        return withoutCurrent;
      }
      return [...withoutCurrent, treatment];
    });
  }, []);
  const value = useMemo(() => ({
    treatments,
    isLoading,
    hasFetched: !isLoading,
    eligibleTreatments,
    registerEligibility
  }), [treatments, isLoading, eligibleTreatments, registerEligibility]);
  return /*#__PURE__*/_jsx(ExperimentContext.Provider, {
    value: value,
    children: children
  });
};
export const useExperimentContext = () => {
  const context = useContext(ExperimentContext);
  if (!context) {
    throw new Error('useExperimentContext must be used within an ExperimentProvider');
  }
  return context;
};

/**
 * Test-only provider that supplies static defaults without calling laboratoryClient.resolve().
 */
export const TestExperimentProvider = ({
  children
}) => {
  const value = useMemo(() => ({
    treatments: {},
    isLoading: false,
    hasFetched: true,
    eligibleTreatments: [],
    registerEligibility: () => {}
  }), []);
  return /*#__PURE__*/_jsx(ExperimentContext.Provider, {
    value: value,
    children: children
  });
};
export const useEligiblePublicTreatments = () => {
  const {
    eligibleTreatments
  } = useExperimentContext();
  return useMemo(() => eligibleTreatments.filter(t => t.isPublic), [eligibleTreatments]);
};